import { Injectable } from '@angular/core';
import { signalbaseUrl } from '../../../environments/environment';
import { HubConnection } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { StorageService } from './storage.service';
import { StatusChange, HubConstant } from '../../shared/model/enum';

@Injectable({
  providedIn: 'root'
})
export class RealtimeService {
  private hubConnectionForSMXHub?: HubConnection;
  public smxapi_connectionId: string = "";

  constructor(private storageService: StorageService) {}

  // Subjects for data updates
  private liveDataUpdates = new Subject<any>();
  public liveDataUpdates$ = this.liveDataUpdates.asObservable();

   private liveDataDetailsUpdates = new Subject<any>();
  public liveDataDetailsUpdates$ = this.liveDataUpdates.asObservable();

  private liveDataUpdatesStage1 = new Subject<any>();
  private liveDataUpdatesStage2 = new Subject<any>();
  private liveDataUpdatesStage3 = new Subject<any>();
  public liveDataUpdatesStage1$ = this.liveDataUpdatesStage1.asObservable();
  public liveDataUpdatesStage2$ = this.liveDataUpdatesStage2.asObservable();
  public liveDataUpdatesStage3$ = this.liveDataUpdatesStage3.asObservable();

  // Other Subjects
  private ondemandRequestDeclined = new Subject<any>();
  public ondemandRequestDeclined$ = this.ondemandRequestDeclined.asObservable();

  private deactivateUser = new Subject<any>();
  public deactivateUser$ = this.deactivateUser.asObservable();

  private permissionChanged = new Subject<any>();
  public permissionChanged$ = this.permissionChanged.asObservable();

  // #region HubConnection Management
  async createHubConnectionForSMX() {
    this.hubConnectionForSMXHub = new signalR.HubConnectionBuilder()
      .withUrl(`${signalbaseUrl}smx-hub`, {
        withCredentials: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => this.storageService.retrieve("authorizationDataIdToken") || '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupSMXHubConnectionHandlers();
    await this.startConnectionForSMXHub();

    this.initializeHubListeners();
  }

  private async startConnectionForSMXHub() {
    if (this.hubConnectionForSMXHub?.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.hubConnectionForSMXHub.start();
        console.log('Connection started');
        this.smxapi_connectionId = this.hubConnectionForSMXHub.connectionId || "";
      } catch (err) {
        console.error('Error while starting connection:', err);
      }
    } else {
      console.log('Connection already established or in progress');
    }
  }

  public disposeSMXHubConnection() {
    if (this.hubConnectionForSMXHub?.state !== signalR.HubConnectionState.Disconnected) {
      this.hubConnectionForSMXHub?.stop()?.then(() => console.log('Connection stopped'))
        .catch(err => console.error('Error while stopping connection:', err));
    }
  }
  // #endregion

  // #region Connection Handlers
  private setupSMXHubConnectionHandlers() {
    this.hubConnectionForSMXHub?.onreconnecting(error => console.warn(`Reconnecting due to error:`, error));
    this.hubConnectionForSMXHub?.onreconnected(connectionId => console.log(`Reconnected with ID: ${connectionId}`));
    this.hubConnectionForSMXHub?.onclose(error => console.error('Connection closed:', error));
  }
  // #endregion

 

 private initializeHubListeners() {
  const hubActions = [
    { func: HubConstant.CompanyCreatedFunction, status: StatusChange.CompanyCreated },
    { func: HubConstant.CompanyUpdatedFunction, status: StatusChange.CompanyUpdated },
    { func: HubConstant.VendorCreatedFunction, status: StatusChange.VendorCreated },
    { func: HubConstant.VendorUpdatedFunction, status: StatusChange.VendorUpdated },
    { func: HubConstant.UserCreatedFunction, status: StatusChange.UserCreated },
    { func: HubConstant.UserUpdatedFunction, status: StatusChange.UserUpdated },
    { func: HubConstant.PartsCreatedFunction, status: StatusChange.PartsCreated },
    { func: HubConstant.PartsUpdatedFunction, status: StatusChange.PartsUpdated },
    { func: HubConstant.ContactCreatedFunction, status: StatusChange.ContactCreated },
    { func: HubConstant.ContactUpdatedFunction, status: StatusChange.ContactUpdated },
    { func: HubConstant.VendorContactCreatedFunction, status: StatusChange.VendorContactCreated },
    { func: HubConstant.VendorContactUpdatedFunction, status: StatusChange.VendorContactUpdated },
    { func: HubConstant.ActivityCreatedFunction, status: StatusChange.ActivityCreated },
    { func: HubConstant.ActivityUpdatedFunction, status: StatusChange.ActivityUpdated },
    { func: HubConstant.UserRoleCreatedFunction, status: StatusChange.UserRoleCreated },
    { func: HubConstant.UserRoleUpdatedFunction, status: StatusChange.UserRoleUpdated },
    { func: HubConstant.CodeCrosswalkCreatedFunction, status: StatusChange.CodeCrosswalkCreated },
    { func: HubConstant.CodeCrosswalkUpdatedFunction, status: StatusChange.CodeCrosswalkUpdated },
    { func: HubConstant.CodeCrosswalkDeletedFunction, status: StatusChange.CodeCrosswalkDeleted },
    { func: HubConstant.CompanyDeletedFunction, status: StatusChange.CompanyDeleted },
    { func: HubConstant.PartsDeletedFunction, status: StatusChange.PartsDeleted },
    { func: HubConstant.vendorDeletedFunction, status: StatusChange.VendorDeleted },
    { func: HubConstant.VendorContactDeletedFunction, status: StatusChange.VendorContactDeleted },
    { func: HubConstant.LeadCreatedFunction, status: StatusChange.LeadCreated },
    { func: HubConstant.LeadUpdatedFunction, status: StatusChange.LeadUpdated },
    { func: HubConstant.ProspectCreatedFunction, status: StatusChange.ProspectCreated },
    { func: HubConstant.ProspectUpdatedFunction, status: StatusChange.ProspectUpdated },
    { func: HubConstant.CustomerCreatedFunction, status: StatusChange.CustomerCreated },
    { func: HubConstant.CustomerUpdatedFunction, status: StatusChange.CustomerUpdated },
    { func: HubConstant.LeadDeletedFunction, status: StatusChange.LeadDeleted },
    { func: HubConstant.ProspectDeletedFunction, status: StatusChange.ProspectDeleted },
    { func: HubConstant.CustomerDeletedFunction, status: StatusChange.CustomerDeleted },
    { func: HubConstant.CompanyDetailsFunction, status: StatusChange.CompanyDetails }, 
    { func: HubConstant.PartsDetailsFunction, status: StatusChange.PartsDetails }, 
    { func: HubConstant.UserDeletedFunction, status: StatusChange.UserDeleted }, 
    { func: HubConstant.ContactDeletedFunction, status: StatusChange.ContactDeleted }, 
  ];

  hubActions.forEach(action => {
    this.hubConnectionForSMXHub?.on(action.func, (res, stageId) => {
      if (!res || Object.keys(res).length === 0) {
        console.warn(`Received empty response for ${action.func}`, { res, stageId });
        return;
      }

      const data = { StatusChange: action.status, response: res, stageId };

      let emittedToStage = false;
      switch (stageId) { 
        case '1':
          this.liveDataUpdatesStage1.next(data);
          emittedToStage = true;
          break;
        case '2':
          this.liveDataUpdatesStage2.next(data);
          emittedToStage = true;
          break;
        case '3':
          this.liveDataUpdatesStage3.next(data);
          emittedToStage = true;
          break;
        case 'All':
          this.liveDataUpdatesStage1.next(data);
          this.liveDataUpdatesStage2.next(data);
          this.liveDataUpdatesStage3.next(data); 
          emittedToStage = true;
          break;
        default:
          if (stageId) console.warn(`Unrecognized stageId: ${stageId}`);
            emittedToStage = true;
          break;
      }

      // Emit to global liveDataUpdates if stage-specific emission happened OR stage is not defined
      if (emittedToStage || !stageId) {
        this.liveDataUpdates.next(data);
      }
    });
  });
}

  // #endregion
}
